/*
 * "Build your kit" add-on UI — ported from mockup/script.js.
 *
 * 1. Enhances every select.odd__native inside [data-odd] into a custom listbox.
 *    The native <select> stays the source of truth, so the field still submits (and validation,
 *    cart and summary logic in main-product-addons.js keep working) even if this never runs.
 * 2. Groups all option fields under one header (the mockup collapsed them behind it; the panel
 *    is now always open so every option is visible without expanding anything). The header's
 *    count shows how many required picks are done.
 * 3. Moves the theme's own variant picker into that panel, so variants and add-ons read as one
 *    set of fields.
 *
 * Phosphor icons in the mockup are replaced with inline SVGs (the theme has no icon font).
 */
(function () {
  if (window.__pdpKitInit) return;
  window.__pdpKitInit = true;

  var SVG = {
    caret:
      '<svg class="odd__caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>',
    check:
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>',
    checkCircle:
      '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><polyline points="8.5 12 11 14.5 15.5 9.5"></polyline></svg>',
  };

  function containerData(el, key, fallback) {
    var container = el.closest("[data-product-addons]");
    return (container && container.dataset[key]) || fallback;
  }

  /* ---------- Optimized dropdowns ---------- */

  // Close callbacks for every enhanced field on the page, so opening one closes the others.
  // Module-level (not per-init) because init() re-runs when <variant-selects> re-renders its
  // markup on a variant change; a per-run array would leave the two generations unaware of
  // each other. Detached entries are pruned on open.
  var oddOpen = [];

  // Unique per enhanced field, so the listbox and its options can carry ids for aria-controls
  // and aria-activedescendant. Has to be a running counter rather than anything derived from
  // the select: <variant-selects> re-renders its markup on every variant change, so the same
  // field is enhanced again and the old ids may still be in the document mid-swap.
  var oddSeq = 0;

  // The field's visible label text, used to name the combobox. Both the add-on fields and the
  // relocated variant picker use `.pdp-opt__label`. The asterisk marking a required field is
  // stripped - it is punctuation, not part of the name.
  function fieldLabelEl(field) {
    var wrap = field.closest(".pdp-opt");
    return wrap ? wrap.querySelector(".pdp-opt__label") : null;
  }

  function enhanceOdd(field) {
    if (field.classList.contains("odd--ready")) return;
    var select = field.querySelector(".odd__native");
    if (!select) return;

    var options = Array.prototype.slice.call(select.options);
    var activeIndex = select.selectedIndex;

    oddSeq++;
    var listId = "odd-list-" + oddSeq;
    var btnId = "odd-btn-" + oddSeq;
    var labelEl = fieldLabelEl(field);
    var labelText = labelEl ? labelEl.textContent.replace(/\*/g, "").trim() : "";

    /*
     * The native <select> is kept as the form value but is visually reduced to a 1px
     * transparent box, so it must leave the tab order and the accessibility tree: a keyboard
     * user was landing on it with no visible focus, and a screen reader saw every option twice
     * (once here, once in the listbox below). `tabindex="-1"` rather than `disabled`, because a
     * disabled select is not submitted and `pick()` still has to set its value and fire change.
     */
    select.setAttribute("tabindex", "-1");
    select.setAttribute("aria-hidden", "true");

    var btn = document.createElement("button");
    btn.type = "button";
    btn.id = btnId;
    btn.className = "odd__btn";
    // ARIA combobox: the button is the control, the <ul> below is the popup it owns, and the
    // active option is pointed at with aria-activedescendant rather than by moving focus.
    btn.setAttribute("role", "combobox");
    btn.setAttribute("aria-haspopup", "listbox");
    btn.setAttribute("aria-expanded", "false");
    btn.setAttribute("aria-controls", listId);

    // The visible <label> points its `for` at the native select, which is now hidden from
    // assistive tech - clicking it would move focus to something invisible. Re-point it at the
    // button so the label works for the control the customer can actually see.
    if (labelEl && labelEl.tagName === "LABEL") {
      labelEl.setAttribute("for", btnId);
    }

    var value = document.createElement("span");
    value.className = "odd__value";
    btn.appendChild(value);
    btn.insertAdjacentHTML("beforeend", SVG.caret);

    var pop = document.createElement("div");
    pop.className = "odd__pop";

    var list = document.createElement("ul");
    list.id = listId;
    list.className = "odd__list";
    list.setAttribute("role", "listbox");
    if (labelText) list.setAttribute("aria-label", labelText);
    pop.appendChild(list);

    // Index of the first option carrying a discount amount (used as the default selection).
    var firstDiscountIndex = -1;

    var items = options.map(function (opt, index) {
      var li = document.createElement("li");
      li.id = listId + "-opt-" + index;
      li.className = "odd__opt";
      li.setAttribute("role", "option");
      li.setAttribute("data-index", String(index));

      var text = document.createElement("span");
      text.textContent = opt.textContent;
      li.appendChild(text);
      li.insertAdjacentHTML("beforeend", SVG.check);

      // Carry the credit/discount amount onto the list item when the option has one.
      // Prefer the explicit data-discount-amount; otherwise derive it from a credit option's
      // negative data-price (cents -> positive dollars).
      var discountAmount = opt.dataset.discountAmount;
      if (!discountAmount && opt.dataset.addonCredit === "true") {
        var creditCents = Math.abs(Number(opt.dataset.price) || 0);
        if (creditCents) discountAmount = creditCents / 100;
      }
      if (discountAmount) {
        li.setAttribute("data-discount-amount", discountAmount);
        if (firstDiscountIndex === -1) firstDiscountIndex = index;
      }

      if (opt.disabled && opt.value === "") {
        // The "Select an option…" placeholder is a prompt, not a choice — hide it.
        li.setAttribute("hidden", "hidden");
      } else {
        li.addEventListener("click", function () {
          pick(index);
        });
        li.addEventListener("mouseenter", function () {
          setActive(index);
        });
      }
      list.appendChild(li);
      return li;
    });

    field.appendChild(btn);
    field.appendChild(pop);
    field.classList.add("odd--ready");

    // For a negative add-on (an option carries a discount amount), default-select the FIRST
    // such option. Runs before main-product-addons.js reads the selection for its summary.
    if (firstDiscountIndex !== -1) {
      select.selectedIndex = firstDiscountIndex;
      activeIndex = firstDiscountIndex;
    }

    function syncButton() {
      var opt = select.options[select.selectedIndex];
      var text = opt ? opt.textContent.trim() : "";
      value.textContent = opt ? opt.textContent : "";
      // Name the control the way the ticket asks - option name plus current value, e.g.
      // "Color, Polished". The visible text only carries the value, so on its own the button
      // was announced without saying WHICH option it is.
      btn.setAttribute("aria-label", labelText ? labelText + ", " + text : text);
      field.classList.toggle("odd--invalid", !!opt && opt.disabled);
      items.forEach(function (li, i) {
        li.classList.toggle("odd__opt--selected", i === select.selectedIndex);
        li.setAttribute("aria-selected", i === select.selectedIndex ? "true" : "false");
      });
    }

    function setActive(index) {
      activeIndex = index;
      items.forEach(function (li, i) {
        li.classList.toggle("odd__opt--active", i === index);
      });
      // Focus never leaves the button, so this is the only thing telling a screen reader which
      // option is current. Without it the highlight moved visually and announced nothing.
      if (index > -1 && items[index]) {
        btn.setAttribute("aria-activedescendant", items[index].id);
      } else {
        btn.removeAttribute("aria-activedescendant");
      }
    }

    function visibleIndexes() {
      return items.reduce(function (acc, li, i) {
        // Skip hidden placeholder and disabled (unselectable) options.
        if (!li.hasAttribute("hidden") && !options[i].disabled) acc.push(i);
        return acc;
      }, []);
    }

    function moveTo(which) {
      var shown = visibleIndexes();
      if (!shown.length) return;
      var index = which === "first" ? shown[0] : shown[shown.length - 1];
      setActive(index);
      items[index].scrollIntoView({ block: "nearest" });
    }

    function move(step) {
      var shown = visibleIndexes();
      if (!shown.length) return;
      var at = shown.indexOf(activeIndex);
      var next = at === -1 ? 0 : (at + step + shown.length) % shown.length;
      setActive(shown[next]);
      items[shown[next]].scrollIntoView({ block: "nearest" });
    }

    function open() {
      // Drop closures left behind by a previous render of this picker before using the list.
      for (var i = oddOpen.length - 1; i >= 0; i--) {
        if (!oddOpen[i].field.isConnected) oddOpen.splice(i, 1);
      }
      oddOpen.forEach(function (other) {
        if (other !== close) other();
      });
      field.classList.add("odd--open");
      btn.setAttribute("aria-expanded", "true");
      setActive(select.selectedIndex);
    }

    function close() {
      field.classList.remove("odd--open");
      btn.setAttribute("aria-expanded", "false");
      // The listbox is hidden now, so a pointer into it would dangle.
      btn.removeAttribute("aria-activedescendant");
    }

    function pick(index) {
      select.selectedIndex = index;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      syncButton();
      close();
      btn.focus();
    }

    btn.addEventListener("click", function () {
      if (field.classList.contains("odd--open")) close();
      else open();
    });

    field.addEventListener("keydown", function (e) {
      if (!field.classList.contains("odd--open")) {
        // ArrowUp opens too: the combobox pattern expects either arrow to open the list.
        if (
          e.key === "ArrowDown" ||
          e.key === "ArrowUp" ||
          e.key === "Enter" ||
          e.key === " "
        ) {
          e.preventDefault();
          open();
        }
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        move(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        move(-1);
      } else if (e.key === "Home") {
        e.preventDefault();
        moveTo("first");
      } else if (e.key === "End") {
        e.preventDefault();
        moveTo("last");
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (activeIndex > -1) pick(activeIndex);
      } else if (e.key === "Escape") {
        close();
        btn.focus();
      } else if (e.key === "Tab") {
        close();
      }
    });

    select.addEventListener("change", syncButton);
    close.field = field;
    oddOpen.push(close);
    syncButton();
  }

  /* ---------- Theme variant picker -> "Build your kit" panel ---------- */

  /*
   * Moves the theme's own variant dropdowns into the kit panel so they read as the first fields
   * of the same grid as the add-ons.
   *
   * The WHOLE `.product-variants` wrapper moves, never the individual fields. <variant-selects>
   * (assets/scripts.js) resolves everything through `this.querySelector(...)` - the option JSON
   * script tags in getVariantData(), `select option:checked` in getSelectedOptionValues() and
   * maybeGetFirstAvailableVariant(), and `this.innerHTML = …` in updateOptionValues() - and its
   * `change` handler is bound to the element itself. Lifting a field out of it would break all
   * of that; moving the ancestor keeps the subtree intact, so no theme behaviour changes.
   *
   * Only the element moves - it is never re-created - so this runs once. Variant changes replace
   * the markup INSIDE <variant-selects>, which is what the observer below re-enhances.
   */
  function relocateVariantPicker(kit) {
    var opts = kit.querySelector(".pdp-opts");
    if (!opts) return;
    var scope = kit.closest(".shopify-section") || document;
    var variants = scope.querySelector(".product-variants");
    if (!variants) return;
    // Already in the panel, or a picker this build does not restyle (button mode, or a product
    // without add-ons, where `odd_style` was not passed and there is no [data-odd] to enhance).
    if (variants.closest(".kit__panel")) return;
    if (!variants.querySelector("variant-selects [data-odd]")) return;

    var sourceBlock = variants.closest(".product-block");
    opts.insertBefore(variants, opts.firstChild);
    // The block the picker came from is now empty; collapse its margin/divider.
    if (sourceBlock) sourceBlock.classList.add("product-block-variants-moved");
    // On a product with no add-ons the panel is hidden until this point, because the picker is
    // the only thing it holds - reveal it now that the picker is actually inside.
    kit.classList.remove("kit--awaiting-picker");
  }

  /*
   * <variant-selects>.updateOptionValues() does `this.innerHTML = …` on every variant change
   * (assets/scripts.js), which throws away the enhanced listbox and leaves the bare <select>
   * behind. Re-enhance whatever replaced it.
   *
   * An observer rather than PUB_SUB_EVENTS.productVariantChange: that event is only published on
   * the "variant exists" path, so the unavailable-combination branch - which also calls
   * updateOptionValues - would be missed. `subtree` is deliberately off, so enhanceOdd building
   * the listbox inside a descendant .odd cannot re-trigger this.
   */
  function watchVariantSelects(kit) {
    kit.querySelectorAll("variant-selects").forEach(function (host) {
      if (host.__pdpKitObserved) return;
      host.__pdpKitObserved = true;
      new MutationObserver(function () {
        enhanceAll();
      }).observe(host, { childList: true });
    });
  }

  /* ---------- Required count in the kit header ---------- */
  // The panel has no collapse any more - it is always open so every option is on the page - so
  // this only keeps the "x of y required selected" readout in sync.
  function initKit(kit) {
    // init() re-runs after a variant re-render; without this each pass would bind another set
    // of change listeners to the same fields.
    if (kit.dataset.kitReady === "true") return;
    kit.dataset.kitReady = "true";
    var kitCount = kit.querySelector(".kit__count");
    if (!kitCount) return;
    var required = kit.querySelectorAll(
      '.odd__native[required], input[type="checkbox"][data-addon-required], input[data-addon-text][data-addon-required]',
    );
    // Nothing is required - a variants-only panel, or add-ons that are all optional. Variant
    // selects are deliberately not counted (they carry no `required`), so the readout would sit
    // at a meaningless "0 of 0 required selected" forever. Drop it and leave the header clean.
    if (!required.length) {
      kitCount.hidden = true;
      return;
    }
    var countTemplate = kit.dataset.kitCount || "[done] of [total] required selected";
    var allSetText = kit.dataset.kitAllSet || "All set";

    function kitSync() {
      var done = 0;
      required.forEach(function (el) {
        if (el.tagName === "SELECT") {
          var opt = el.options[el.selectedIndex];
          if (opt && !opt.disabled && el.value) done++;
        } else if (el.type === "checkbox") {
          if (el.checked) done++;
        } else if (el.value.trim()) {
          // Text add-on, trimmed so spaces alone do not tick it off - matching the
          // add-to-cart check in main-product-addons.js.
          done++;
        }
      });
      var all = required.length > 0 && done === required.length;
      kitCount.classList.toggle("kit__count--done", all);
      if (all) {
        kitCount.innerHTML = SVG.checkCircle + " " + allSetText;
      } else {
        kitCount.textContent = countTemplate
          .replace("[done]", done)
          .replace("[total]", required.length);
      }
    }

    required.forEach(function (el) {
      el.addEventListener("change", kitSync);
      // A text input only fires `change` on blur, so the counter would lag a keystroke behind
      // the field it is counting.
      if (el.matches("input[data-addon-text]")) el.addEventListener("input", kitSync);
    });
    kitSync();
  }

  // enhanceOdd() skips anything already carrying .odd--ready, so this is safe to re-run.
  function enhanceAll() {
    document.querySelectorAll("[data-odd]").forEach(enhanceOdd);
  }

  // Registered once, not per init(), so a re-render does not stack duplicate handlers.
  document.addEventListener("click", function (e) {
    if (e.target.closest("[data-odd]")) return;
    oddOpen.forEach(function (close) {
      close();
    });
  });

  // Re-runnable: relocateVariantPicker, watchVariantSelects and initKit each no-op once done,
  // and enhanceAll only touches fields that are not enhanced yet.
  function init() {
    document.querySelectorAll(".product-addons.kit").forEach(function (kit) {
      relocateVariantPicker(kit);
      watchVariantSelects(kit);
    });
    enhanceAll();
    document.querySelectorAll(".product-addons.kit").forEach(initKit);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
